import { omit, pick } from 'lodash-es'
import { nanoid } from 'nanoid'

import { useGetUserQuery } from 'common'
import { getDefaultRules } from 'components/availability/form/utils'
import { EVENT_TYPES_LIST_ITEM_ATTRIBUTES, STANDARD_EVENT_TYPE } from 'constants/event_types'
import { featureTypeIsKind, getFeatureType } from 'services/event_type_restriction_service'

const getDefaultAvailabilityRules = (primaryRuleId, timezone) => ({
  primaryRuleId,
  rules: [
    {
      id: primaryRuleId,
      owner_id: primaryRuleId,
      rules: getDefaultRules(),
      timezone
    }
  ]
})

export const useGetDefaultAvailabilityRules = () => {
  const { data: { primary_availability_rule_id: primaryRuleId, timezone } } = useGetUserQuery()
  const defaultAvailabilityRules = getDefaultAvailabilityRules(primaryRuleId, timezone)

  return defaultAvailabilityRules?.rules?.[0]
}

export const getSidebarOptions = ({
  ownerType,
  ownerId,
  eventTypeOwner,
  kind,
  poolingType,
  type,
  groupUuid = undefined
}) => {
  const featureType = getFeatureType(kind, poolingType)

  const commonOptions = {
    type: type || STANDARD_EVENT_TYPE,
    eventTypeOwner,
    groupUuid,
    uniqueId: nanoid() // used to trigger artificial rerender of the right rail, when we reopen with same params
  }

  if (ownerType === 'Page') {
    return {
      ...commonOptions,
      ownerType,
      ownerId,
      kind,
      poolingType
    }
  }

  if (!featureTypeIsKind(featureType)) {
    return {
      ...commonOptions,
      ownerType: 'Organization',
      ownerId: null,
      kind: 'solo',
      poolingType
    }
  }

  if (ownerId !== undefined) {
    return {
      ...commonOptions,
      ownerType,
      ownerId,
      kind
    }
  }

  return {
    ...commonOptions,
    ownerType,
    ownerId: 'me',
    kind
  }
}

export const getETPickerProfileEventTypesUrl = ({ profileType, userId, profileId }) => {
  switch (profileType) {
    case 'User':
      return `/api/users/${userId}/event_types?scope=personal`
    case 'Page':
      return `/api/teams/${profileId}/event_types?scope=team_single`
    case 'Organization':
      return `/api/users/${userId}/event_types?scope=participated_organization`
    default:
      throw Error('Wrong profile type')
  }
}

export const workflowsState = (responseData) => {
  if (responseData.workflows.collection.length > 0) { return 'list' }
  if (responseData.owner_has_any_workflows) { return 'empty_state' }

  return 'hero_banner'
}

export const entityToSuggestion = (entity, type) => ({
  id: [type, entity.id].join('-'),
  type,
  entity
})

const getBookingPath = (object) => {
  if (!object.profile) return object.booking_url

  return `/${object.profile.slug}/${object.slug}`
}

const isLocationOnMemberships = (object) => {
  if (object.pooling_type === 'round_robin') { return true }
  if (object.pooling_type === 'multi_pool' && object.user_pools?.every(({ pooling_type: pt }) => pt === 'round_robin')) { return true }

  return false
}

const getLocationType = (object) => {
  if (isLocationOnMemberships(object)) { return null }

  return object.location_configurations[0]?.kind || null
}

const getLocationTypesCount = (object) => {
  if (isLocationOnMemberships(object)) { return null }

  return object.location_configurations.length
}

// The function below is used to convert the response
// from the EventTypes::ItemPresenter into the format of the EventTypes::List::ItemPresenter
// so that it can be merged into the eventTypesList data.
// We omit the 'policy' attribute because it is different for list items,
// and we add other attributes because they are missing from the item presenter.
export const convertETItemToETListItem = (data) => ({
  ...omit(pick(data, ...EVENT_TYPES_LIST_ITEM_ATTRIBUTES), 'policy'),
  booking_path: getBookingPath(data),
  duration: data.duration_description,
  duration_minutes: data.duration,
  duration_options: data.duration_options,
  active_availability_data: data.active_availability_data,
  location_type: getLocationType(data),
  location_types_count: getLocationTypesCount(data),
  owner_id: data.owner_id || data?.profile?.owner_id
})
